import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Pencil } from "lucide-react";
import { api } from "@/lib/api";
import { useProjectCtx } from "@/context/ProjectContext";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { EmptyState } from "@/components/EmptyState";
import { PercentBar } from "@/components/PercentBar";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { fmtDate, fmtNum, todayISO } from "@/lib/format";
import { getErrorMessage } from "@/lib/errors";

export default function Construction() {
  const { projectId, canWrite } = useProjectCtx();
  const canEdit = canWrite("CONSTRUCTION");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/projects/${projectId}/construction`);
      setItems(data);
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => { load(); }, [load]);
  const leafItems = items.filter((i) => !i.is_group);

  return (
    <div className="space-y-4" data-testid="construction-page">
      <PageHeader title="Construction — Site Progress" description={canEdit ? "Log today's output or correct the running total." : "Read-only for your project role."} />
      <div className="rounded-xl border border-border bg-card">
        {!loading && leafItems.length === 0 ? (
          <EmptyState title="No construction activities" testId="construction-empty-state" />
        ) : (
          <Table>
            <TableHeader>
              <TableRow><TableHead>Code</TableHead><TableHead>Activity</TableHead><TableHead>UoM</TableHead><TableHead>Scope</TableHead><TableHead>Progress</TableHead><TableHead>Sched. end</TableHead><TableHead>Manpower</TableHead><TableHead></TableHead></TableRow>
            </TableHeader>
            <TableBody>
              {leafItems.map((i) => {
                const c = i.construction || {};
                const scope = Number(i.quantity || 0);
                const actualPct = scope ? Number(c.cum_actual_qty || 0) / scope : 0;
                const plannedPct = scope ? Number(c.cum_planned_qty || 0) / scope : 0;
                return (
                  <TableRow key={i.id} data-testid={`construction-row-${i.id}`}>
                    <TableCell className="font-mono text-xs">{i.code}</TableCell>
                    <TableCell className="text-foreground">{i.name}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">{i.uom || "—"}</TableCell>
                    <TableCell className="font-mono text-xs">{fmtNum(c.cum_actual_qty)} / {fmtNum(scope)}</TableCell>
                    <TableCell className="min-w-[130px]">
                      <div className="flex items-center gap-2">
                        <PercentBar actual={actualPct} planned={plannedPct} testId={`construction-percent-bar-${i.id}`} />
                        <span className="font-mono text-xs">{(actualPct * 100).toFixed(0)}%</span>
                      </div>
                    </TableCell>
                    <TableCell className="font-mono text-xs text-muted-foreground">{fmtDate(c.scheduled_end_date)}</TableCell>
                    <TableCell className="font-mono text-xs text-muted-foreground">{fmtNum(c.present_manpower)}</TableCell>
                    <TableCell className="text-right">
                      {canEdit && (
                        <Button size="sm" variant="ghost" onClick={() => setEditing(i)} data-testid={`edit-construction-btn-${i.id}`}><Pencil className="h-3.5 w-3.5" /></Button>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </div>
      {editing && <ConstructionDialog projectId={projectId} item={editing} onClose={() => setEditing(null)} onSaved={load} />}
    </div>
  );
}

function ConstructionDialog({ projectId, item, onClose, onSaved }) {
  const c = item.construction || {};
  const [mode, setMode] = useState("today");
  const [amount, setAmount] = useState("");
  const [form, setForm] = useState({
    entry_date: todayISO(), note: "", scheduled_start_date: c.scheduled_start_date || "", scheduled_end_date: c.scheduled_end_date || "",
    actual_start_date: c.actual_start_date || "", forecast_end_date: c.forecast_end_date || "", actual_end_date: c.actual_end_date || "",
    cum_planned_qty: c.cum_planned_qty ?? "", present_manpower: c.present_manpower ?? "", avg_output_per_day: c.avg_output_per_day ?? "",
    status_remarks: c.status_remarks || "",
  });
  const set = (f) => (e) => setForm({ ...form, [f]: e.target.value });

  const submit = async () => {
    try {
      const payload = {
        entry_date: form.entry_date, note: form.note || null,
        scheduled_start_date: form.scheduled_start_date || null, scheduled_end_date: form.scheduled_end_date || null,
        actual_start_date: form.actual_start_date || null, forecast_end_date: form.forecast_end_date || null, actual_end_date: form.actual_end_date || null,
        cum_planned_qty: form.cum_planned_qty === "" ? null : Number(form.cum_planned_qty),
        present_manpower: form.present_manpower === "" ? null : Number(form.present_manpower),
        avg_output_per_day: form.avg_output_per_day === "" ? null : Number(form.avg_output_per_day),
        status_remarks: form.status_remarks || null,
      };
      if (amount !== "") {
        if (mode === "today") payload.progress_qty = Number(amount);
        else payload.cumulative_qty = Number(amount);
      }
      await api.patch(`/projects/${projectId}/construction/${item.id}`, payload);
      toast.success("Construction progress updated");
      onSaved();
      onClose();
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[85vh] overflow-y-auto" data-testid="construction-dialog">
        <DialogHeader><DialogTitle>{item.code} — {item.name}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="rounded-lg border border-gold-500/30 bg-gold-500/5 p-3">
            <Label className="text-xs uppercase tracking-wider text-gold-600 dark:text-gold-400">Daily quantity update — pick one</Label>
            <RadioGroup value={mode} onValueChange={setMode} className="mt-2 grid grid-cols-2 gap-2">
              <label className="flex items-center gap-2 rounded-md border border-border p-2 text-sm">
                <RadioGroupItem value="today" data-testid="daily-output-mode-today" /> Log today's output
              </label>
              <label className="flex items-center gap-2 rounded-md border border-border p-2 text-sm">
                <RadioGroupItem value="running" data-testid="daily-output-mode-running" /> Correct running total
              </label>
            </RadioGroup>
            <Input
              type="number" className="mt-2" data-testid="construction-amount-input"
              value={amount} onChange={(e) => setAmount(e.target.value)}
              placeholder={mode === "today" ? "Quantity completed today" : "New corrected cumulative total"}
            />
            <p className="mt-1 text-[11px] text-muted-foreground">
              Current cumulative: <span className="font-mono">{fmtNum(c.cum_actual_qty)}</span> {item.uom}
            </p>
          </div>

          <div><Label>Entry date</Label><Input type="date" data-testid="construction-entry-date-input" value={form.entry_date} onChange={set("entry_date")} className="mt-1.5" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Scheduled start</Label><Input type="date" data-testid="construction-sched-start-input" value={form.scheduled_start_date} onChange={set("scheduled_start_date")} className="mt-1.5" /></div>
            <div><Label>Scheduled end</Label><Input type="date" data-testid="construction-sched-end-input" value={form.scheduled_end_date} onChange={set("scheduled_end_date")} className="mt-1.5" /></div>
            <div><Label>Actual start</Label><Input type="date" data-testid="construction-actual-start-input" value={form.actual_start_date} onChange={set("actual_start_date")} className="mt-1.5" /></div>
            <div><Label>Forecast end</Label><Input type="date" data-testid="construction-forecast-end-input" value={form.forecast_end_date} onChange={set("forecast_end_date")} className="mt-1.5" /></div>
            <div><Label>Actual end</Label><Input type="date" data-testid="construction-actual-end-input" value={form.actual_end_date} onChange={set("actual_end_date")} className="mt-1.5" /></div>
            <div><Label>Cum. planned qty</Label><Input type="number" data-testid="construction-cum-planned-input" value={form.cum_planned_qty} onChange={set("cum_planned_qty")} className="mt-1.5" /></div>
            <div><Label>Present manpower</Label><Input type="number" data-testid="construction-manpower-input" value={form.present_manpower} onChange={set("present_manpower")} className="mt-1.5" /></div>
            <div><Label>Avg output/day</Label><Input type="number" data-testid="construction-avg-output-input" value={form.avg_output_per_day} onChange={set("avg_output_per_day")} className="mt-1.5" /></div>
          </div>
          <div><Label>Status remarks</Label><Textarea data-testid="construction-status-remarks-input" value={form.status_remarks} onChange={set("status_remarks")} className="mt-1.5" /></div>
          <div><Label>Note for this entry</Label><Input data-testid="construction-note-input" value={form.note} onChange={set("note")} className="mt-1.5" /></div>
        </div>
        <DialogFooter><Button onClick={submit} data-testid="construction-submit-button">Save</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
